import type {jobsPayload} from 'src/types/referrer-portal';
import type {ReferralSelectedQuestionnaire} from 'src/types/referral-v2';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useParams, useHistory} from 'src/rerouter';
import {useDispatch, useSelector} from 'react-redux';
import {
  COUNTRY_CODES,
  showLabelWithAsterisk,
} from 'src/utils/referral-external';
import classify from 'src/utils/classify';
import {validateEmail} from 'src/utils/index';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {validatePhone, getCountryFromPhone} from 'src/utils/phone';
import {RECEIVE_PROGRAM_QUESTIONS} from 'src/action-creators/referrer-portal/user-form';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardActions,
  Button,
  Input,
  Dropdown,
  Toggle,
  FileUpload,
  SubTitleLarge,
  BodySmall,
  FormLabelSmall,
  PanelFooter,
  Tooltip,
  Icon,
  type FileObject,
} from '@spaced-out/ui-design-system/lib/components/index';
import AssignJobPanel from 'src/components/referrer-portal/user-form/assign-job';
import css from 'src/components/referrer-portal/user-form/form-container.css';


const initialFormData = {
  candidate_name: '',
  candidate_email: '',
  candidate_contact: ['+1', ''],
  candidate_recommendation: '',
  candidate_resume: '',
} as const;

const UserForm = ({
  isIframe,
  handleSubmitForm,
  shouldAllowOptionalResume,
}: {
  isSubmitting: boolean;
  isIframe: boolean;
  handleSubmitForm: (formData: {[key: string]: string}) => unknown;
  shouldAllowOptionalResume: boolean;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {t} = useLabelTranslation();
  const {
    location: {query},
  } = history;

  const programQuestions: ReferralSelectedQuestionnaire[] | null | undefined =
    useSelector((state) => state.referrerPortal.program_questions);
  const customQuestions: ReferralSelectedQuestionnaire[] | null | undefined =
    programQuestions?.filter(
      (question: ReferralSelectedQuestionnaire) =>
        question.is_visible &&
        question.response_field.includes('custom_question'),
    );

  const [formData, setFormData] = React.useState(initialFormData);
  const [errors, setErrors] = React.useState(new Map());
  const [selectedJob, setSelectedJob] = React.useState(
    getDefaultValue('job', query),
  );
  const [isToggle, setIsToggle] = React.useState(!!query.job_id || false);
  const [showJobPanel, setShowJobPanel] = React.useState(false);
  const [selectedAgency, setSelectedAgency] = React.useState(
    getDefaultValue('agency', query),
  );
  const [selectedProgram, setSelectedProgram] = React.useState(
    getDefaultValue('program', query),
  );

  const validateForm = () => {
    const newErrors = new Map();

    if (isToggle && !selectedJob) {
      newErrors.set('job_id', true);
    }

    for (const fieldName in formData) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly candidate_name: ""; readonly candidate_email: ""; readonly candidate_contact: readonly ["+1", ""]; readonly candidate_recommendation: ""; readonly candidate_resume: ""; }'.
      const value = formData[fieldName];
      const customQuestion = customQuestions?.find(
        (question: ReferralSelectedQuestionnaire) =>
          question.response_field === fieldName,
      );

      if (fieldName === 'candidate_email' && !validateEmail(value)) {
        newErrors.set(fieldName, true);
      }

      if (fieldName === 'candidate_contact') {
        const [countryCode, phone] = value ?? [COUNTRY_CODES[0].key, ''];
        const phoneNumberWithCountryCode = countryCode + phone || '+1';
        const countryShortCode = getCountryFromPhone(
          phoneNumberWithCountryCode,
        );
        if (!validatePhone(phone, countryShortCode || 'US')) {
          newErrors.set(fieldName, true);
        }
      }
      // custom question validation
      if (
        customQuestion &&
        !customQuestion.is_optional &&
        selectedJob &&
        !value
      ) {
        newErrors.set(fieldName, true);
      }
      if (
        fieldName === 'candidate_resume' &&
        !shouldAllowOptionalResume &&
        !value
      ) {
        newErrors.set(fieldName, true);
      }
      if (fieldName !== 'candidate_resume' && !value) {
        newErrors.set(fieldName, true);
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = () => {
    const error = validateForm();
    if (error.size > 0) {
      return;
    }
    const formPayloadData = {
      ...formData,
      candidate_contact: formData.candidate_contact.join(''),
      agency_id: selectedAgency?.agency_id ?? '',
      program_id: selectedProgram?.program_id ?? '',
      job_id: selectedJob?.job_id ?? '',
      job_title: selectedJob?.job_title ?? '',
    } as const;
    handleSubmitForm(formPayloadData);
  };

  const handleChange = (
    fieldName: string,
    value: string | null | undefined | string[],
  ) => {
    const updatedFormData = {...formData, [fieldName]: value} as const;
    setFormData(updatedFormData);
  };

  const handleResetJob = () => {
    if (query.job_id) {
      return;
    }
    setSelectedAgency(null);
    setSelectedProgram(null);
    setSelectedJob(null);
    dispatch({
      //@ts-expect-error
      type: RECEIVE_PROGRAM_QUESTIONS,
      //@ts-expect-error
      payload: null,
    });
  };

  // @ts-expect-error - TS7031 - Binding element 'checked' implicitly has an 'any' type.
  const onToggleChange = ({checked}) => {
    if (!checked) {
      handleResetJob();
    }
    setIsToggle(checked);
  };

  const handleAssignJob = (payload: jobsPayload) => {
    setSelectedJob({job_id: payload.job_id, job_title: payload.job_title});
    setSelectedAgency({
      agency_id: payload.agency_id,
      agency_name: payload.agency_name,
    });
    setSelectedProgram({
      program_id: payload.program_id,
      program_name: payload.program_name,
    });
  };

  const jobValueText = `${selectedJob ? selectedJob.job_title : ''} ${
    selectedAgency?.agency_name ? `| ${selectedAgency.agency_name}` : ''
  } ${
    selectedProgram?.program_name ? `| ${selectedProgram.program_name}` : ''
  }`;

  return (
    <div
      className={classify(css.formContainer, {
        [css.panelContainer]: isIframe,
      })}
    >
      <Card
        classNames={{
          wrapper: classify(css.formCard, {[css.PanelContent]: isIframe}),
        }}
      >
        <CardHeader>
          <SubTitleLarge>
            {t('Referral_Details', 'Referral Details')}
          </SubTitleLarge>
        </CardHeader>
        <CardContent className={css.formContent}>
          <Card classNames={{wrapper: css.jobSelectInputBox}}>
            <div className={css.toggle}>
              {showLabelWithAsterisk(
                t(
                  'Do_you_want_to_refer_to_a_specific_job',
                  'Do you want to refer to a specific job',
                ),
                isToggle,
              )}
              <Toggle
                disabled={!!query.job_id}
                checked={isToggle}
                onChange={onToggleChange}
              />
            </div>
            {isToggle &&
              (selectedJob ? (
                <Input
                  classNames={{box: css.selectedJob}}
                  value={jobValueText}
                  iconRightName={query.job_id ? '' : 'xmark'}
                  onIconRightClick={handleResetJob}
                />
              ) : (
                <Button
                  iconRightName="chevron-right"
                  type="tertiary"
                  classNames={{wrapper: css.selectJob}}
                  onClick={() => setShowJobPanel(true)}
                >
                  {t('Select_job', 'Select job')}
                </Button>
              ))}
            {errors.has('job_id') && (
              <BodySmall color="danger">
                {t('Please_select_a_job', 'Please select a job')}
              </BodySmall>
            )}
            <AssignJobPanel
              isOpen={showJobPanel}
              onClose={() => setShowJobPanel(false)}
              handleAssignJob={handleAssignJob}
            />
          </Card>
          <div className={css.basicDetails}>
            <Input
              value={formData.candidate_name}
              placeholder={`${t('Enter_name', 'Enter name')} (${t(
                'max_30_characters',
                'max 30 characters',
              )})`}
              required
              label={t('Full_Name', 'Full Name')}
              error={errors.has('candidate_name')}
              errorText={t('Field_is_required', 'Field is required')}
              maxLength={30}
              onChange={(event) =>
                handleChange('candidate_name', event.target.value)
              }
            />
            <Input
              value={formData.candidate_email}
              placeholder={t('Enter_email', 'Enter email')}
              required
              label={
                <FormLabelSmall>
                  {t('Email', 'Email')}
                  <Tooltip
                    body={`${t(
                      'This_email_will_be_used_to_send_communication_to_the_candidate',
                      'This email will be used to send communication to the candidate',
                    )}.`}
                  >
                    <Icon name="info-circle" size="small" />
                  </Tooltip>
                </FormLabelSmall>
              }
              error={errors.has('candidate_email')}
              errorText={t('Enter_a_valid_email_id', 'Enter a valid email id')}
              onChange={(event) =>
                handleChange('candidate_email', event.target.value)
              }
            />
            <PhoneInputQuestion
              required={true}
              // @ts-expect-error - TS4104 - The type 'readonly ["+1", ""]' is 'readonly' and cannot be assigned to the mutable type 'string[]'.
              value={formData.candidate_contact}
              error={errors.has('candidate_contact')}
              onChange={(value) => handleChange('candidate_contact', value)}
            />
          </div>
          <Input
            value={formData.candidate_recommendation}
            placeholder={t('Enter_details', 'Enter details')}
            required
            label={`${t(
              'What_qualifies_your_reference_for_this_position',
              'What qualifies your reference for this position',
            )}?`}
            error={errors.has('candidate_recommendation')}
            errorText={t('Field_is_required', 'Field is required')}
            onChange={(event) =>
              handleChange('candidate_recommendation', event.target.value)
            }
          />
          <FileInputQuestion
            shouldAllowOptionalResume={shouldAllowOptionalResume}
            error={errors.has('candidate_resume')}
            onChange={(fileName) => handleChange('candidate_resume', fileName)}
            value={formData.candidate_resume}
          />
          {selectedJob &&
            customQuestions?.map((question: ReferralSelectedQuestionnaire) => {
              return (
                <Input
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly candidate_name: ""; readonly candidate_email: ""; readonly candidate_contact: readonly ["+1", ""]; readonly candidate_recommendation: ""; readonly candidate_resume: ""; }'.
                  value={formData[question.response_field]}
                  required={!question.is_optional}
                  label={question.value}
                  error={errors.has(question.response_field)}
                  errorText={t('Field_is_required', 'Field is required')}
                  onChange={(event) =>
                    handleChange(question.response_field, event.target.value)
                  }
                />
              );
            })}
        </CardContent>
        {!isIframe && (
          <CardFooter>
            <CardActions>
              <Button onClick={handleSubmit}>{t('Submit', 'Submit')}</Button>
            </CardActions>
          </CardFooter>
        )}
      </Card>
      {isIframe && (
        <PanelFooter>
          <Button onClick={handleSubmit}>{t('Submit', 'Submit')}</Button>
        </PanelFooter>
      )}
    </div>
  );
};

export default UserForm;

export const PhoneInputQuestion = ({
  value,
  onChange,
  error,
  required = false,
}: {
  value: string[];
  onChange: (value: string[]) => unknown;
  error: boolean;
  required?: boolean;
}) => {
  const {t} = useLabelTranslation();
  const defaultSelectedCountryCode = COUNTRY_CODES.find(
    (country) => country.key === '+1',
  );
  const [countryCode, phone] = value ?? [defaultSelectedCountryCode?.key, ''];
  const selectedCountryCode =
    COUNTRY_CODES.find((country) => country.key === countryCode) ||
    defaultSelectedCountryCode;

  return (
    <div>
      {showLabelWithAsterisk(t('Phone_Number', 'Phone Number'), required)}
      <div className={css.phoneInput}>
        <Dropdown
          dropdownInputText={selectedCountryCode?.key}
          menu={{
            allowSearch: true,
            options: COUNTRY_CODES,
            selectedOption: selectedCountryCode,
          }}
          onChange={(selectedOption) => onChange([selectedOption.key, phone])}
          classNames={{
            wrapper: css.countrySelect,
          }}
        />
        <Input
          value={phone}
          placeholder={t('Enter_Phone', 'Enter Phone')}
          onChange={(event) => onChange([countryCode, event.target.value])}
          error={error}
          errorText={t(
            'Enter_a_valid_phone_number',
            'Enter a valid phone number',
          )}
          type="tel"
        />
      </div>
    </div>
  );
};

const FileInputQuestion = ({
  onChange,
  error,
  shouldAllowOptionalResume,
}: {
  value: string | string[];
  onChange: (fileName: string) => unknown;
  error: boolean;
  shouldAllowOptionalResume: boolean;
}): React.ReactElement<React.ComponentProps<React.ElementType>> => {
  const {t} = useLabelTranslation();
  const ref = React.useRef(null);
  const dispatch = useDispatch();
  const {referralLinkId = ''} = useParams();

  const handleFileUpload = (fileObject: FileObject) => {
    const fileType = fileObject.file.type.split('/')[1];
    const formData = new FormData();
    // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
    ref.current?.moveFileToProgress(30);
    const otherFields = {
      file_type: fileType,
      link_id: referralLinkId,
    } as const;
    Object.keys(otherFields).forEach((key) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly file_type: any; readonly link_id: string; }'.
      formData.append(key, otherFields[key]);
    });
    formData.append('image', fileObject.file);
    // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
    ref.current?.moveFileToProgress(fileObject.id, 60);
    dispatch(reduxApi.post('form/upload-resume', formData)).then(
      (file_name) => {
        onChange(file_name);
        // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
        ref.current?.moveFileToProgress(fileObject.id, 80);
        // @ts-expect-error - TS2339 - Property 'moveFileToProgress' does not exist on type 'never'.
        ref.current?.moveFileToProgress(fileObject.id, 100);
        // @ts-expect-error - TS2339 - Property 'moveFileToSuccess' does not exist on type 'never'.
        ref.current?.moveFileToSuccess(fileObject.id, 'Successfully uploaded');
      },
    );
  };

  return (
    <div className={css.flexColumn}>
      {showLabelWithAsterisk(
        t('Attach_Resume', 'Attach Resume'),
        !shouldAllowOptionalResume,
      )}
      <FileUpload
        label=""
        ref={ref}
        required={true}
        instruction={t('Drag_drop_the_file_here', 'Drag & drop the file here')}
        secondaryInstruction={`${t(
          'Types',
          'Types',
        )}: .pdf, .doc, .docx, .txt, .png, .jpg  ${t(
          'Max_size',
          'Max. size',
        )}: 5mb ${t('Min_size', 'Min. size')} 10kb`}
        maxSize={5000000} //in bytes
        accept={{
          'image/pdf': ['.pdf', '.doc', '.docx', '.txt', '.png', '.jpg'],
        }}
        onFileClear={() => {}}
        onValidFilesDrop={(file) => {
          handleFileUpload(file[0]);
        }}
      />
      {error && (
        <BodySmall color="danger">
          {t('Please_upload_a_resume', 'Please upload a resume')}
        </BodySmall>
      )}
    </div>
  );
};

const getDefaultValue = (
  fieldName: string,
  query: {
    [key: string]: string;
  },
):
  | {
      [key: string]: string;
    }
  | null
  | undefined => {
  switch (fieldName) {
    case 'agency': {
      if (query.agency_id) {
        return {
          agency_id: query.agency_id,
          agency_name: query.agency_name,
        };
      }
      return null;
    }
    case 'program': {
      if (query.program_id) {
        return {
          program_id: query.program_id,
          program_name: query.program_name,
        };
      }
      return null;
    }
    case 'job': {
      {
        if (query.job_id) {
          return {
            job_id: query.job_id,
            job_title: query.job_title,
          };
        }
        return null;
      }
    }
    default:
      return null;
  }
};
